import React, { useState, useEffect, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Plugin,
  Chart,
  ArcElement,
  ChartTypeRegistry,
  TooltipItem,
  ChartOptions,
} from 'chart.js';
import CustomLegend2 from './CustomLegend2';
import TooltipPopover from 'components/TooltipPopover';
import DownloadMenu from 'components/DownloadMenu';
import { noDataPlugin } from '../../../utils';

interface DoughnutChartPlugin extends Plugin<'doughnut'> {}

const ChartDataLabelsPlugin = ChartDataLabels as unknown as DoughnutChartPlugin;
const noData = noDataPlugin as DoughnutChartPlugin;
interface DemandData {
  Feature_Group: string;
  feature_group_total_demand: number;
  demand_percentage: number;
}

interface DemandFGProps {
  data: {
    data: DemandData[];
  };
}

const colors = [
  'rgba(34, 34, 34, 0.80)',
  'rgba(89, 89, 89, 0.8)',
  'rgba(144, 144, 144, 0.8)',
  'rgba(50, 215, 111, 0.8)',
  'rgba(3, 197, 255, 0.8)',
  'rgba(84, 79, 197, 0.8)',
  'rgba(254, 106, 53, 0.8)',
  'rgba(107, 138, 188, 0.8)',
  'rgba(213, 104, 251, 0.8)',
  'rgba(47, 224, 202, 0.8)',
  'rgba(250, 75, 66, 0.8)',
  'rgba(254, 181, 106, 0.8)',
  'rgba(254, 181, 106, 0.8)',
];

const DemandFG: React.FC<DemandFGProps> = ({ data }) => {
  const labels = useMemo(
    () => data.data.map((item) => item.Feature_Group),
    [data.data]
  );
  const chartData = useMemo(
    () => data.data.map((item) => item.demand_percentage),
    [data.data]
  );

  const [visibility, setVisibility] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initialVisibility = labels.reduce(
      (acc: Record<string, boolean>, label) => {
        acc[label] = label !== 'uncategorized';
        return acc;
      },
      {}
    );

    setVisibility(initialVisibility);
  }, [labels]);

  const options: ChartOptions<'doughnut'> & {
    plugins: { extendedGridLinePlugin?: boolean };
  } = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      extendedGridLinePlugin: false,
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context: TooltipItem<'doughnut'>) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += context.parsed.toFixed(2) + '%';
            }
            return label;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        display: 'auto',
        color: '#1A1A1A',
        font: {
          weight: 'normal' as const,
          size: 12,
        },
        formatter: (value: number) => {
          return `${Math.round(value * 100) / 100}%`;
        },
        padding: {
          top: 2,
          bottom: 2,
        },
        offset: 16,
      },
    },
    cutout: '50%',
    layout: {
      padding: {
        top: 35,
        bottom: 35,
      },
    },
  };

  const afterDraw = (chart: Chart<keyof ChartTypeRegistry>) => {
    const ctx = chart.ctx;
    const lineLength = 18.78; // Length of the lines

    if (chart.data.labels) {
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        chart.getDatasetMeta(datasetIndex).data.forEach((element) => {
          const arcElement = element as unknown as ArcElement;
          const radius = arcElement.outerRadius;

          // Calculate the angle for the line
          const angle =
            arcElement.startAngle +
            (arcElement.endAngle - arcElement.startAngle) / 2;
          const startX = chart.width / 2 + radius * Math.cos(angle);
          const startY = chart.height / 2 + radius * Math.sin(angle);
          const endX = startX + lineLength * Math.cos(angle);
          const endY = startY + lineLength * Math.sin(angle);

          // Draw the line from the outer edge of the doughnut
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.strokeStyle = '#333';
          ctx.stroke();
        });
      });
    }
  };

  const afterDrawPlugin: Plugin<'doughnut'> = {
    id: 'afterDrawPlugin',
    afterDraw: (chart) => afterDraw(chart as Chart<keyof ChartTypeRegistry>),
  };

  const DoughnutIcon = ({ color }: { color: string }) => (
    <svg
      width="16"
      height="12"
      viewBox="0 0 16 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ stroke: color }}
    >
      <path
        d="M6 6H10"
        strokeOpacity="0.8"
        strokeWidth="12"
        strokeLinecap="round"
      />
    </svg>
  );

  const legends = labels.map((label, index) => ({
    label,
    color: colors[index % colors.length],
    icon: <DoughnutIcon color={colors[index % colors.length]} />,
  }));

  const handleLegendClick = (label: string) => {
    setVisibility((prevState) => ({
      ...prevState,
      [label]: !prevState[label],
    }));
  };

  const filteredData = chartData.map((value, index) =>
    visibility[labels[index]] ? value : 0
  );

  // Data for XLS file download
  const headers = ['Feature Group', 'Demand Percentage'];
  const fileName = 'demand_by_feature_group';
  const columns = [chartData];

  return (
    <div
      id="Demand_by_Feature_Group"
      className="bg-light rounded-2xl shadow-sm mt-8"
    >
      <div className="flex justify-between items-center border-b border-success mb-2">
        <div>
          <TooltipPopover
            content="consumer_intent_page.product_and_feature_analysis_tab.demand_by_feature_group"
            type="info"
            className="top-2 right-[70px]"
            side="top"
          >
            <h2 className="text-primarygray text-2xl font-bold mb-1 pl-4 pt-2">
              Demand by Feature Group
            </h2>
          </TooltipPopover>
          <h3 className="text-primarygray text-sm font-bold mb-2 pl-4">
            How consumers search for feature groups of your products
          </h3>
        </div>
        <DownloadMenu
          id="downloadDropdownMenu-Demand_by_Feature_Group"
          XLSParams={{
            filename: fileName,
            headers: headers,
            labels: labels,
            columns: columns,
          }}
          imageParam="Demand_by_Feature_Group"
        />
      </div>
      <div className="flex relative">
        <div className="flex relative pl-2 py-6 justify-center items-center md:h-[295px] 2xl:w-[85%] xl:w-[85%] md:w-[85%] w-[85%]">
          <Doughnut
            data={{
              labels,
              datasets: [
                {
                  data: filteredData,
                  backgroundColor: colors,
                  hoverBackgroundColor: colors,
                  borderWidth: 1,
                },
              ],
            }}
            options={options}
            plugins={[ChartDataLabelsPlugin, afterDrawPlugin, noData]}
          />
        </div>
        <div className="flex-none text-xs flex flex-col items-start pl-4 pr-4 py-6">
          <CustomLegend2
            brandsData={legends}
            onLegendClick={handleLegendClick}
            visibility={visibility}
          />
        </div>
      </div>
    </div>
  );
};

export default DemandFG;
